import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, attendanceAPI, taskAPI, userAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FiFolder, FiCheckSquare, FiClock, FiTrendingUp, FiUsers, FiDollarSign, FiAlertTriangle, FiCalendar } from 'react-icons/fi';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import './Dashboard.css';

const PRIORITY_COLORS = { low:'#22c55e', medium:'#f59e0b', high:'#ef4444', critical:'#7c3aed' };
const STATUS_BG = { active:'#d1fae5', planning:'#ede9fe', 'on-hold':'#fef3c7', completed:'#dbeafe', cancelled:'#f1f5f9' };
const STATUS_TX = { active:'#065f46', planning:'#5b21b6', 'on-hold':'#92400e', completed:'#1e40af', cancelled:'#475569' };

const fmtTime = d => d ? new Date(d).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }) : '—';
const daysUntil = d => Math.ceil((new Date(d) - Date.now()) / 86400000);
const pct = (done, total) => total ? Math.round((done / total) * 100) : 0;

export default function Dashboard() {
  const { user } = useAuth();
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(false);
  const [attBusy,  setAttBusy]  = useState(false);
  const [celebrations, setCelebrations] = useState([]);
  const [taskFilter, setTaskFilter] = useState('today'); // today | overdue | all

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const [dashR] = await Promise.all([dashboardAPI.getSummary()]);
      setData(dashR.data.data);
      if (user?.role !== 'client') {
        try { const cR = await userAPI.getCelebrations(); setCelebrations(cR.data.data || []); } catch {}
      }
    } catch { setError(true); }
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleCheckIn  = async () => { setAttBusy(true); try { await attendanceAPI.checkIn({}); await load(); } catch(e){ alert(e.response?.data?.message || 'Error'); } setAttBusy(false); };
  const handleCheckOut = async () => { setAttBusy(true); try { await attendanceAPI.checkOut();  await load(); } catch(e){ alert(e.response?.data?.message || 'Error'); } setAttBusy(false); };
  const handleBreakStart = async () => { setAttBusy(true); try { await attendanceAPI.startBreak({}); await load(); } catch(e){ alert(e.response?.data?.message || 'Error'); } setAttBusy(false); };
  const handleBreakEnd   = async () => { setAttBusy(true); try { await attendanceAPI.endBreak();      await load(); } catch(e){ alert(e.response?.data?.message || 'Error'); } setAttBusy(false); };

  const quickComplete = async (taskId) => {
    try { await taskAPI.update(taskId, { status: 'completed' }); await load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Cannot complete task'); }
  };

  if (user?.role === 'client') return <Navigate to="/client-portal" replace />;

  if (loading) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'60vh',gap:16}}>
      <div style={{width:44,height:44,border:'3px solid #e2e8f0',borderTop:'3px solid #6366f1',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
      <span style={{color:'#94a3b8',fontSize:14,fontFamily:"'Sora',sans-serif"}}>Loading your workspace…</span>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error) return (
    <div style={{textAlign:'center',padding:60,fontFamily:"'Sora',sans-serif"}}>
      <div style={{fontSize:40,marginBottom:16}}>⚠️</div>
      <h2 style={{color:'#0f172a',marginBottom:8}}>Failed to load dashboard</h2>
      <p style={{color:'#64748b',marginBottom:24}}>Could not connect to the server.</p>
      <button onClick={load} style={{padding:'10px 24px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'#fff',border:'none',borderRadius:10,fontWeight:600,cursor:'pointer',fontSize:14}}>Retry</button>
    </div>
  );

  const { todayAtt, breakPolicy, tasks, projects, minutesLoggedToday, pendingLeaves, deadlineProjects, finance, teamStatus, unreadCount } = data || {};
  const att = todayAtt;
  const isCheckedIn  = !!att?.checkIn;
  const isCheckedOut = !!att?.checkOut;
  const isOnBreak    = !!att?.activeBreak;
  const breakUsed    = breakPolicy?.usedBreakMins || 0;
  const breakMax     = breakPolicy?.maxBreakMins || 60;
  const breakPct     = Math.min(100, Math.round((breakUsed / breakMax) * 100));
  const breakColor   = breakPct >= 100 ? '#ef4444' : breakPct >= 75 ? '#f59e0b' : '#22c55e';

  const todayTasks     = tasks?.today || [];
  const missedYest     = tasks?.missedYesterday || [];
  const overdueTasks   = tasks?.overdue || [];
  const allTasks       = tasks?.all || [];
  const completedToday = todayTasks.filter(t => t.status === 'completed').length;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const S = {
    card: { background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px #0000000a', border:'1px solid #f1f5f9' },
    sm:   { background:'#fff', borderRadius:12, padding:16, boxShadow:'0 1px 8px #00000008', border:'1px solid #f1f5f9' },
  };

  const taskListData = taskFilter === 'today' ? todayTasks : taskFilter === 'overdue' ? overdueTasks : allTasks.slice(0, 15);

  // ── Charts data ────────────────────────────────────────
  const taskChartData = [
    { name:'Todo',        value: allTasks.filter(t=>t.status==='todo').length,        color:'#94a3b8' },
    { name:'In Progress', value: allTasks.filter(t=>t.status==='in-progress').length, color:'#f59e0b' },
    { name:'In Review',   value: allTasks.filter(t=>t.status==='review').length,      color:'#6366f1' },
    { name:'Completed',   value: allTasks.filter(t=>t.status==='completed').length,   color:'#22c55e' },
  ].filter(d => d.value > 0);

  return (
    <div style={{ maxWidth:1440, margin:'0 auto', fontFamily:"'Sora',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap'); @keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <div style={{ background:'linear-gradient(135deg,#0f172a 0%,#1e293b 55%,#312e81 100%)', borderRadius:24, padding:'28px 32px', marginBottom:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:20 }}>

          {/* Left: greeting + date */}
          <div>
            <h1 style={{ margin:0, fontSize:26, fontWeight:800, color:'#f8fafc' }}>{greeting}, {user?.name?.split(' ')[0]} 👋</h1>
            <p style={{ margin:'4px 0 0', color:'#94a3b8', fontSize:13 }}>{new Date().toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</p>
            {unreadCount > 0 && (
              <Link to="/notifications" style={{ display:'inline-flex', alignItems:'center', gap:6, marginTop:8, fontSize:12, color:'#a5b4fc', textDecoration:'none', background:'rgba(165,180,252,.12)', padding:'4px 12px', borderRadius:8, border:'1px solid rgba(165,180,252,.2)' }}>
                🔔 {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </Link>
            )}
          </div>

          {/* Right: attendance card */}
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'stretch' }}>

            {/* Attendance status + buttons */}
            <div style={{ background:'rgba(255,255,255,.07)', borderRadius:16, padding:'16px 20px', minWidth:220 }}>
              <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600, marginBottom:6, letterSpacing:.5 }}>TODAY'S ATTENDANCE</div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background: isCheckedOut ? '#22c55e' : isOnBreak ? '#f59e0b' : isCheckedIn ? '#6366f1' : '#64748b', animation: isCheckedIn && !isCheckedOut ? 'pulse 2s infinite' : 'none' }}/>
                <span style={{ fontWeight:700, color:'#f1f5f9', fontSize:14 }}>
                  {isCheckedOut ? 'Done for today' : isOnBreak ? 'On Break' : isCheckedIn ? 'Working' : 'Not Checked In'}
                </span>
              </div>
              {att?.checkIn && <div style={{ fontSize:11, color:'#64748b', marginBottom:8 }}>In: {fmtTime(att.checkIn)}{att.checkOut ? ` · Out: ${fmtTime(att.checkOut)}` : ''}</div>}
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {!isCheckedIn && (
                  <button onClick={handleCheckIn} disabled={attBusy} style={{ padding:'7px 16px', background:'#22c55e', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer', opacity:attBusy?.5:1 }}>Check In</button>
                )}
                {isCheckedIn && !isCheckedOut && !isOnBreak && (
                  <>
                    <button onClick={handleBreakStart} disabled={attBusy || breakPct >= 100} style={{ padding:'7px 14px', background: breakPct >= 100 ? '#94a3b8' : '#f59e0b', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor: breakPct >= 100 ? 'not-allowed' : 'pointer' }}>Break</button>
                    <button onClick={handleCheckOut} disabled={attBusy} style={{ padding:'7px 14px', background:'#ef4444', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>Check Out</button>
                  </>
                )}
                {isOnBreak && (
                  <button onClick={handleBreakEnd} disabled={attBusy} style={{ padding:'7px 16px', background:'#6366f1', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>End Break</button>
                )}
              </div>
            </div>

            {/* Break time meter */}
            {isCheckedIn && !isCheckedOut && (
              <div style={{ background:'rgba(255,255,255,.07)', borderRadius:16, padding:'16px 20px', minWidth:160 }}>
                <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600, marginBottom:8, letterSpacing:.5 }}>BREAK TIME</div>
                <div style={{ position:'relative', height:8, background:'rgba(255,255,255,.1)', borderRadius:4, overflow:'hidden', marginBottom:8 }}>
                  <div style={{ position:'absolute', left:0, top:0, height:'100%', width:`${breakPct}%`, background: breakColor, borderRadius:4, transition:'width .5s' }}/>
                </div>
                <div style={{ fontSize:12, color: breakPct >= 100 ? '#fca5a5' : '#94a3b8' }}>
                  {Math.round(breakUsed)} / {breakMax} min used
                </div>
                {breakPct >= 100 && <div style={{ fontSize:11, color:'#fca5a5', marginTop:4, fontWeight:600 }}>Limit reached</div>}
                {breakPct < 100 && <div style={{ fontSize:11, color:'#86efac', marginTop:4 }}>{breakMax - Math.round(breakUsed)} min remaining</div>}
              </div>
            )}

            {/* Time logged today */}
            <div style={{ background:'rgba(255,255,255,.07)', borderRadius:16, padding:'16px 20px', minWidth:130 }}>
              <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600, marginBottom:6, letterSpacing:.5 }}>TIME LOGGED</div>
              <div style={{ fontSize:22, fontWeight:800, color:'#f1f5f9' }}>{(minutesLoggedToday / 60).toFixed(1)}h</div>
              <Link to="/time-tracking" style={{ fontSize:11, color:'#a5b4fc', textDecoration:'none', marginTop:4, display:'block' }}>+ Log time</Link>
            </div>

            {/* Pending leaves badge (admin) */}
            {(user?.role === 'admin' || user?.role === 'manager') && pendingLeaves > 0 && (
              <div style={{ background:'rgba(245,158,11,.15)', border:'1px solid rgba(245,158,11,.3)', borderRadius:16, padding:'16px 20px', textAlign:'center' }}>
                <div style={{ fontSize:11, color:'#f59e0b', fontWeight:600, marginBottom:4, letterSpacing:.5 }}>PENDING LEAVES</div>
                <div style={{ fontWeight:800, color:'#f59e0b', fontSize:26 }}>{pendingLeaves}</div>
                <Link to="/leaves" style={{ fontSize:11, color:'#f59e0b', textDecoration:'none' }}>Review →</Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Alert banners ────────────────────────────────────── */}
      {missedYest.length > 0 && (
        <div style={{ background:'#fff1f2', border:'2px solid #fca5a5', borderRadius:14, padding:'14px 18px', marginBottom:16, display:'flex', gap:12, alignItems:'flex-start' }}>
          <span style={{ fontSize:20 }}>🚨</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, color:'#991b1b', fontSize:14, marginBottom:6 }}>{missedYest.length} task{missedYest.length!==1?'s were':' was'} due yesterday and not completed</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {missedYest.slice(0,5).map(t => (
                <Link key={t.id} to={`/tasks/${t.id}`} style={{ fontSize:12, fontWeight:600, color:'#991b1b', textDecoration:'none', background:'#fecaca', padding:'3px 10px', borderRadius:8, border:'1px solid #f87171' }}>{t.title}</Link>
              ))}
            </div>
          </div>
        </div>
      )}
      {deadlineProjects?.length > 0 && (
        <div style={{ background:'#fffbeb', border:'2px solid #fcd34d', borderRadius:14, padding:'14px 18px', marginBottom:16, display:'flex', gap:12, alignItems:'flex-start' }}>
          <span style={{ fontSize:20 }}>⏰</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, color:'#92400e', fontSize:14, marginBottom:6 }}>{deadlineProjects.length} project{deadlineProjects.length!==1?'s':''} due within 7 days</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {deadlineProjects.map(p => (
                <Link key={p.id} to={`/projects/${p.id}`} style={{ fontSize:12, fontWeight:600, color:'#92400e', textDecoration:'none', background:'#fde68a', padding:'3px 10px', borderRadius:8, border:'1px solid #fbbf24' }}>
                  {p.name} — {daysUntil(p.deadline)}d left
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── KPI cards ─────────────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))', gap:14, marginBottom:24 }}>
        {[
          { i:<FiFolder/>,       l:'Projects',    v: projects?.length || 0,                   sub:`${projects?.filter(p=>p.status==='active').length||0} active`,    c:'#6366f1', to:'/projects' },
          { i:<FiCheckSquare/>,  l:"Today's Tasks",v: todayTasks.length,                      sub:`${completedToday} completed`,                                      c:'#22c55e', to:'/tasks' },
          { i:<FiAlertTriangle/>,l:'Overdue',      v: overdueTasks.length,                    sub:'need attention',                                                   c: overdueTasks.length>0?'#ef4444':'#94a3b8', to:'/tasks' },
          { i:<FiClock/>,        l:'Hours Today',  v:`${(minutesLoggedToday/60).toFixed(1)}h`, sub:'time tracked',                                                    c:'#f59e0b', to:'/time-tracking' },
          ...(user?.role === 'admin' && finance ? [
            { i:<FiDollarSign/>, l:'Revenue',      v:`₹${(finance.totalRevenue/1000).toFixed(0)}k`, sub:`₹${(finance.pendingAmount/1000).toFixed(0)}k pending`,     c:'#10b981', to:'/invoices' },
          ] : []),
          ...(teamStatus ? [
            { i:<FiUsers/>,      l:'Team Today',   v:`${teamStatus.present}/${teamStatus.total}`, sub:`${teamStatus.absent} absent · ${teamStatus.onBreak} on break`, c:'#0ea5e9', to:'/attendance' },
          ] : []),
        ].map(s => (
          <Link key={s.l} to={s.to} style={{ ...S.sm, textDecoration:'none', borderLeft:`4px solid ${s.c}`, transition:'all .2s' }}
            onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'}
            onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}>
            <div style={{ color:s.c, fontSize:20, marginBottom:8 }}>{s.i}</div>
            <div style={{ fontSize:26, fontWeight:800, color:'#0f172a' }}>{s.v}</div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>{s.l}</div>
            <div style={{ fontSize:11, color:s.c, marginTop:3, fontWeight:600 }}>{s.sub}</div>
          </Link>
        ))}
      </div>

      {/* ── Main grid ─────────────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>

        {/* Tasks panel */}
        <div style={S.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>Tasks</h3>
            <div style={{ display:'flex', gap:6 }}>
              {['today','overdue','all'].map(f => (
                <button key={f} onClick={() => setTaskFilter(f)}
                  style={{ padding:'4px 12px', borderRadius:8, border:'1px solid', fontSize:11, fontWeight:600, cursor:'pointer',
                    background: taskFilter===f ? '#6366f1' : '#f8fafc',
                    color: taskFilter===f ? '#fff' : '#64748b',
                    borderColor: taskFilter===f ? '#6366f1' : '#e2e8f0' }}>
                  {f === 'today' ? "Today" : f === 'overdue' ? `Overdue${overdueTasks.length ? ` (${overdueTasks.length})` : ''}` : 'All'}
                </button>
              ))}
            </div>
          </div>

          {taskFilter === 'today' && todayTasks.length === 0 && (
            <div style={{ textAlign:'center', padding:'24px 0', color:'#94a3b8', fontSize:13 }}>
              <div style={{ fontSize:28, marginBottom:8 }}>✅</div>No tasks due today
            </div>
          )}
          {taskFilter === 'overdue' && overdueTasks.length === 0 && (
            <div style={{ textAlign:'center', padding:'24px 0', color:'#94a3b8', fontSize:13 }}>
              <div style={{ fontSize:28, marginBottom:8 }}>🎉</div>No overdue tasks
            </div>
          )}

          <div style={{ maxHeight:320, overflowY:'auto' }}>
            {taskListData.map(t => (
              <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid #f8fafc' }}>
                {t.status !== 'completed' && taskFilter === 'today' && (
                  <button title="Mark done" onClick={() => quickComplete(t.id)}
                    style={{ width:18, height:18, borderRadius:4, border:'2px solid #e2e8f0', background:'#fff', cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
                  </button>
                )}
                {(t.status === 'completed' || taskFilter !== 'today') && (
                  <div style={{ width:8, height:8, borderRadius:'50%', background: PRIORITY_COLORS[t.priority] || '#94a3b8', flexShrink:0 }}/>
                )}
                <Link to={`/tasks/${t.id}`} style={{ flex:1, minWidth:0, textDecoration:'none', color:'inherit' }}>
                  <div style={{ fontSize:13, fontWeight:600, color: t.status==='completed' ? '#94a3b8' : '#0f172a', textDecoration: t.status==='completed' ? 'line-through' : 'none', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.title}</div>
                  <div style={{ fontSize:11, color:'#94a3b8', marginTop:1 }}>
                    {t.project?.name}{t.dueDate ? ` · due ${new Date(t.dueDate).toLocaleDateString()}` : ''}
                  </div>
                </Link>
                <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:6, flexShrink:0,
                  background: t.status==='completed'?'#d1fae5':t.status==='in-progress'?'#fef3c7':t.status==='review'?'#ede9fe':'#f1f5f9',
                  color:      t.status==='completed'?'#065f46':t.status==='in-progress'?'#92400e':t.status==='review'?'#5b21b6':'#475569' }}>
                  {t.status}
                </span>
              </div>
            ))}
          </div>
          <Link to="/tasks" style={{ display:'block', textAlign:'center', marginTop:12, fontSize:12, color:'#6366f1', fontWeight:600, textDecoration:'none' }}>View all tasks →</Link>
        </div>

        {/* Projects panel */}
        <div style={S.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>My Projects</h3>
            <Link to="/projects" style={{ fontSize:13, color:'#6366f1', fontWeight:600, textDecoration:'none' }}>View all →</Link>
          </div>
          {(!projects || projects.length === 0) && (
            <div style={{ textAlign:'center', padding:'24px 0', color:'#94a3b8', fontSize:13 }}>No projects yet</div>
          )}
          <div style={{ maxHeight:340, overflowY:'auto' }}>
            {(projects || []).map(p => (
              <Link key={p.id} to={`/projects/${p.id}`} style={{ display:'block', padding:'10px 0', borderBottom:'1px solid #f8fafc', textDecoration:'none', color:'inherit' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:'#0f172a', flex:1, minWidth:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.name}</div>
                  <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:6, background: STATUS_BG[p.status]||'#f1f5f9', color: STATUS_TX[p.status]||'#475569', flexShrink:0, marginLeft:8 }}>{p.status}</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ flex:1, height:5, background:'#f1f5f9', borderRadius:3, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${p.progress}%`, background: p.progress===100?'#22c55e':p.progress>50?'#6366f1':'#f59e0b', borderRadius:3, transition:'width .5s' }}/>
                  </div>
                  <span style={{ fontSize:11, color:'#64748b', fontWeight:600, flexShrink:0 }}>{p.progress}%</span>
                  <span style={{ fontSize:11, color:'#94a3b8', flexShrink:0 }}>{p.completed}/{p.taskCount} tasks</span>
                </div>
                {p.deadline && (
                  <div style={{ fontSize:11, color: daysUntil(p.deadline) <= 3 ? '#ef4444' : '#94a3b8', marginTop:3 }}>
                    {daysUntil(p.deadline) <= 0 ? '⚠️ Overdue' : `Due in ${daysUntil(p.deadline)} days`}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ── Second row: chart + team status + finance ────────── */}
      <div style={{ display:'grid', gridTemplateColumns: finance || teamStatus ? '1fr 1fr' : '1fr', gap:20, marginBottom:20 }}>

        {/* Task chart */}
        {taskChartData.length > 0 && (
          <div style={S.card}>
            <h3 style={{ margin:'0 0 4px', fontSize:15, fontWeight:700 }}>Task Distribution</h3>
            <p style={{ margin:'0 0 12px', fontSize:12, color:'#94a3b8' }}>Across {allTasks.length} tasks</p>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={taskChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={78} paddingAngle={3} dataKey="value">
                  {taskChartData.map((d,i) => <Cell key={i} fill={d.color}/>)}
                </Pie>
                <Tooltip formatter={(v,n) => [`${v} tasks`, n]} contentStyle={{ borderRadius:10, border:'none', boxShadow:'0 4px 20px #0000001a', fontSize:13 }}/>
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:12, paddingTop:8 }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Finance widget (admin) */}
        {finance && (
          <div style={S.card}>
            <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:700 }}>Finance Overview</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[
                { l:'Total Revenue', v:`₹${(finance.totalRevenue/1000).toFixed(1)}k`, c:'#22c55e', bg:'#f0fdf4' },
                { l:'Pending Amount', v:`₹${(finance.pendingAmount/1000).toFixed(1)}k`, c:'#f59e0b', bg:'#fffbeb' },
                { l:'Overdue Amount', v:`₹${(finance.overdueAmount/1000).toFixed(1)}k`, c:'#ef4444', bg:'#fff1f2' },
                { l:'Month Expenses', v:`₹${(finance.monthExpenses/1000).toFixed(1)}k`, c:'#6366f1', bg:'#eff6ff' },
              ].map(f => (
                <div key={f.l} style={{ background:f.bg, borderRadius:12, padding:'14px 16px' }}>
                  <div style={{ fontSize:18, fontWeight:800, color:f.c }}>{f.v}</div>
                  <div style={{ fontSize:11, color:'#64748b', marginTop:3 }}>{f.l}</div>
                </div>
              ))}
            </div>
            <Link to="/invoices" style={{ display:'block', textAlign:'center', marginTop:14, fontSize:12, color:'#6366f1', fontWeight:600, textDecoration:'none' }}>Manage invoices →</Link>
          </div>
        )}

        {/* Team status (admin/manager) */}
        {teamStatus && !finance && (
          <div style={S.card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Team Today</h3>
              <div style={{ display:'flex', gap:10, fontSize:12 }}>
                <span style={{ color:'#22c55e', fontWeight:600 }}>{teamStatus.present} working</span>
                <span style={{ color:'#f59e0b', fontWeight:600 }}>{teamStatus.onBreak} on break</span>
                <span style={{ color:'#ef4444', fontWeight:600 }}>{teamStatus.absent} absent</span>
              </div>
            </div>
            <div style={{ maxHeight:220, overflowY:'auto', display:'flex', flexWrap:'wrap', gap:8 }}>
              {(teamStatus.records || []).map(r => (
                <div key={r.employeeId} title={`${r.name} — ${r.status}`}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', borderRadius:8, background:'#f8fafc', border:'1px solid #f1f5f9' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background: r.status==='working'?'#22c55e':r.status==='on-break'?'#f59e0b':r.status==='checked-out'?'#94a3b8':'#ef4444', flexShrink:0 }}/>
                  <span style={{ fontSize:12, color:'#374151', fontWeight:500 }}>{r.name?.split(' ')[0]}</span>
                </div>
              ))}
            </div>
            <Link to="/attendance" style={{ display:'block', textAlign:'center', marginTop:12, fontSize:12, color:'#6366f1', fontWeight:600, textDecoration:'none' }}>Full attendance →</Link>
          </div>
        )}
      </div>

      {/* ── Admin: team status when finance also shown ────────── */}
      {teamStatus && finance && (
        <div style={{ ...S.card, marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Team Today</h3>
            <div style={{ display:'flex', gap:14, fontSize:12 }}>
              <span style={{ color:'#22c55e', fontWeight:600 }}>{teamStatus.present} working</span>
              <span style={{ color:'#f59e0b', fontWeight:600 }}>{teamStatus.onBreak} on break</span>
              <span style={{ color:'#94a3b8', fontWeight:600 }}>{teamStatus.checkedOut} checked out</span>
              <span style={{ color:'#ef4444', fontWeight:600 }}>{teamStatus.absent} absent</span>
            </div>
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {(teamStatus.records || []).map(r => (
              <div key={r.employeeId} title={`${r.name} — ${r.status}${r.checkIn ? ' · In: ' + fmtTime(r.checkIn) : ''}`}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:10, background:'#f8fafc', border:'1px solid #f1f5f9' }}>
                <div style={{ width:8, height:8, borderRadius:'50%', flexShrink:0, background: r.status==='working'?'#22c55e':r.status==='on-break'?'#f59e0b':r.status==='checked-out'?'#94a3b8':'#ef4444' }}/>
                <span style={{ fontSize:12, color:'#374151', fontWeight:500 }}>{r.name?.split(' ')[0]}</span>
                <span style={{ fontSize:10, color:'#94a3b8' }}>{r.position ? `· ${r.position}` : ''}</span>
              </div>
            ))}
          </div>
          <Link to="/attendance" style={{ display:'block', textAlign:'center', marginTop:12, fontSize:12, color:'#6366f1', fontWeight:600, textDecoration:'none' }}>Full attendance report →</Link>
        </div>
      )}

      {/* ── Celebrations ─────────────────────────────────────── */}
      {celebrations.length > 0 && (
        <div style={{ background:'linear-gradient(135deg,#fdf2f8,#fce7f3)', border:'2px solid #f9a8d4', borderRadius:16, padding:'16px 20px', marginBottom:20 }}>
          <div style={{ fontWeight:800, color:'#831843', fontSize:14, marginBottom:10 }}>🎉 Upcoming Celebrations</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {celebrations.map((c,i) => (
              <div key={i} style={{ background:'#fff', borderRadius:12, padding:'10px 14px', border:'1px solid #fbcfe8', display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:20 }}>{c.type==='birthday'?'🎂':'🎊'}</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:'#831843' }}>{c.user.name}</div>
                  <div style={{ fontSize:11, color:'#db2777' }}>
                    {c.type==='birthday'?'Birthday':'Anniversary'}{c.type==='anniversary'?` (${c.years}yr)`:''} — {c.daysFrom===0?'Today!':c.daysFrom===1?'Tomorrow':`in ${c.daysFrom} days`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Quick links ───────────────────────────────────────── */}
      {user?.role !== 'client' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14 }}>
          {[
            { l:'Attendance',    sub:'Check in / out',        to:'/attendance',   c:'#22c55e', i:'⏱' },
            { l:'Apply Leave',   sub:'Request time off',      to:'/leaves',        c:'#8b5cf6', i:'🌴' },
            { l:'Log Time',      sub:'Track billable hours',  to:'/time-tracking', c:'#f59e0b', i:'📊' },
            { l:'Daily Report',  sub:'Submit EOD report',     to:'/daily-reports', c:'#0ea5e9', i:'📝' },
            ...(user?.role === 'admin' ? [
              { l:'Invoices',    sub:'Billing & payments',    to:'/invoices',      c:'#10b981', i:'💰' },
              { l:'Team',        sub:'Manage employees',      to:'/team',          c:'#6366f1', i:'👥' },
            ] : []),
          ].map(q => (
            <Link key={q.l} to={q.to}
              style={{ ...S.sm, textDecoration:'none', display:'flex', alignItems:'center', gap:14, borderLeft:`4px solid ${q.c}`, transition:'all .2s' }}
              onMouseEnter={e=>e.currentTarget.style.transform='translateX(3px)'}
              onMouseLeave={e=>e.currentTarget.style.transform='translateX(0)'}>
              <span style={{ fontSize:24 }}>{q.i}</span>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:'#0f172a' }}>{q.l}</div>
                <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>{q.sub}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
